import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { Eye, EyeOff } from "lucide-react";
import { Link } from "react-router-dom";
import Loader from "./loader/loader.js";

import axios from "axios";

const SignupForm = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  const onSubmit = async (data) => {
    setLoading(true);

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/signup`, data);
      setLoading(false);
    } catch (err) {
      console.log(err);
      setErrorMessage(
        err?.response?.data?.error || "An unexpected error occurred.",
      );
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-center text-gray-900">
            Create Account
          </h2>
        </div>

        {errorMessage && (
          <div className="mb-4 p-4 bg-red-500 text-white rounded-lg shadow-md">
            <strong>Error: </strong>
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <div>
              <label
                htmlFor="username"
                className="block text-sm font-medium text-gray-700"
              >
                Username
              </label>
              <div className="mt-1">
                <Controller
                  name="username"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Username is required",
                    minLength: {
                      value: 3,
                      message: "Username must be at least 3 characters",
                    },
                  }}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="username"
                      type="text"
                      autoComplete="username"
                      className={`block w-full rounded-lg border ${
                        errors.username ? "border-red-500" : "border-gray-300"
                      } px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500`}
                      placeholder="johndoe"
                    />
                  )}
                />
                {errors.username && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.username.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="role"
                className="block mt-4 text-sm font-medium text-gray-700"
              >
                Role
              </label>
              <div className="mt-1">
                <Controller
                  name="role"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "Role is required",
                  }}
                  render={({ field }) => (
                    <select
                      {...field}
                      id="role"
                      className={`block w-full rounded-lg border ${
                        errors.role ? "border-red-500" : "border-gray-300"
                      } px-4 py-3 text-gray-900 focus:border-blue-500 focus:ring-blue-500`}
                    >
                      <option value="" disabled>
                        Select a role
                      </option>
                      <option value="CREATOR">CREATOR</option>
                      <option value="ADMIN">ADMIN</option>
                      <option value="APPROVER">APPROVER</option>
                    </select>
                  )}
                />
                {errors.role && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.role.message}
                  </p>
                )}
              </div>
            </div>

            <label
              htmlFor="email"
              className="block mt-4 text-sm font-medium text-gray-700"
            >
              Email address
            </label>
            <div className="mt-1">
              <Controller
                name="email"
                control={control}
                defaultValue=""
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: "Please enter a valid email",
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`block w-full rounded-lg border ${
                      errors.email ? "border-red-500" : "border-gray-300"
                    } px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="you@example.com"
                  />
                )}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.email.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-700"
            >
              Password
            </label>
            <div className="mt-1 relative">
              <Controller
                name="password"
                control={control}
                defaultValue=""
                rules={{
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters",
                  },
                }}
                render={({ field }) => (
                  <input
                    {...field}
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="new-password"
                    className={`block w-full rounded-lg border ${
                      errors.password ? "border-red-500" : "border-gray-300"
                    } px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-blue-500`}
                    placeholder="••••••••"
                  />
                )}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              {errors.password && (
                <p className="mt-1 text-sm text-red-500">
                  {errors.password.message}
                </p>
              )}
            </div>
          </div>

          <button
            type="submit"
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {loading ? <Loader /> : "Sign up"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{" "}
            <Link
              to="/"
              href="#"
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupForm;
